import {
  BlisterHeightType,
  StructureForBlisterDocument,
  useStructureWallLossFactorQuery,
  useUpdateBlisterHeightTypeMutation,
  useUpdateStructureMutation,
  useUpdateWallLossFactorMutation,
} from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { EditWallLossFactor } from '../WallLossFactor/EditWallLossFactor';
import { CalculationMethodSelector } from './CalculationMethodSelector';
import {
  actionsStyles,
  closeIconStyles,
  dialogStyles,
  FloatingLine,
  paperStyles,
  tabStyles,
  titleStyles,
} from './styles';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useHavePermission } from '@/hooks';
import { Permissions } from '@/types';

export const PlatformSettings = () => {
  const structureBlisterHeightType = useAtomValue(state.structureBlisterHeightType);
  const [blisterHeightType, setBlisterHeightType] = useState<BlisterHeightType | undefined>(
    structureBlisterHeightType
  );
  const [open, setOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});
  const [currentFactor, setCurrentFactor] = useState<string | undefined>(undefined);
  const [updatedFactor, setUpdatedFactor] = useState<string | undefined>(undefined);
  const [updateOverrides, setUpdateOverrides] = useState<boolean>(false);
  const [updateWallLoss, { loading: wallLossUpdating }] = useUpdateWallLossFactorMutation({
    refetchQueries: [StructureForBlisterDocument],
  });
  const canAccessBlisterHeightType = useHavePermission(
    Permissions.UPDATE_STRUCTURE_BLISTER_HEIGHT_TYPE
  );

  const { data: structureData, loading: structureWallLossLoading } =
    useStructureWallLossFactorQuery({
      variables: { structureId },
      skip: !structureId,
    });

  const [updateBlisterHeightTypeForStructure, { loading: updateBlisterHeightTypeLoading }] =
    useUpdateBlisterHeightTypeMutation();

  const [updateStructure] = useUpdateStructureMutation();

  useEffect(() => {
    const factor = structureData?.structure?.wallLossFactor;
    setCurrentFactor(factor ? String(factor) : undefined);
  }, [setCurrentFactor, structureData]);

  const handleUpdateWallLoss = async () => {
    if (structureId && updatedFactor) {
      try {
        await updateWallLoss({
          variables: {
            wallLossFactor: parseInt(updatedFactor),
            structureId: structureId,
          },
        });
        setCurrentFactor(updatedFactor);
        showSnackBar('Wall loss factor updated successfully.', 'success');
      } catch (error) {
        showSnackBar('Failed to update wall loss factor', 'error');
      }
    }
  };

  const updateBlisterHeightType = async () => {
    if (structureId && blisterHeightType) {
      try {
        await updateStructure({
          variables: {
            input: {
              structureId,
              blisterHeightType: blisterHeightType as BlisterHeightType,
              lastModifiedFor: 'Update blister height type',
            },
          },
        });
        await updateBlisterHeightTypeForStructure({
          variables: {
            input: {
              structureId,
              heightType: blisterHeightType,
              resetOverridden: updateOverrides,
            },
          },
        });
        setCurrentFactor(updatedFactor);
        showSnackBar('Blister height type updated successfully.', 'success');
      } catch (error) {
        showSnackBar('Failed to update blister height type', 'error');
      }
    }
  };

  const handleConfirm = async () => {
    if (selectedTabIndex === 0) {
      await handleUpdateWallLoss();
    } else if (selectedTabIndex === 1) {
      await updateBlisterHeightType();
    }
    setOpen(false);
  };

  const tabs = useMemo(
    () => [
      {
        index: 0,
        label: 'Wall loss',
        component: (
          <EditWallLossFactor
            currentFactor={currentFactor}
            setCurrentFactor={setCurrentFactor}
            setUpdatedFactor={setUpdatedFactor}
            loading={structureWallLossLoading || wallLossUpdating}
          />
        ),
      },
      {
        index: 1,
        label: 'Blister Classification Criteria',
        component: (
          <CalculationMethodSelector
            updateOverrides={updateOverrides}
            setUpdateOverrides={setUpdateOverrides}
            blisterHeightType={blisterHeightType}
            setBlisterHeightType={setBlisterHeightType}
            loading={structureWallLossLoading || updateBlisterHeightTypeLoading}
          />
        ),
        disabled: !canAccessBlisterHeightType,
      },
    ],
    [
      blisterHeightType,
      canAccessBlisterHeightType,
      currentFactor,
      structureWallLossLoading,
      updateBlisterHeightTypeLoading,
      updateOverrides,
      wallLossUpdating,
    ]
  );

  const handleClose = () => {
    setOpen(false);
    setSelectedTabIndex(0);
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={
          <AbyssIcon iconName="gear-outlined" style={{ height: '1.6rem', width: '1.6rem' }} />
        }
        onClick={() => setOpen(true)}
      >
        Platform settings
      </Button>
      <GenericDialog
        open={open}
        onClose={handleClose}
        title="Platform settings"
        showCloseIcon
        closeIconStyles={closeIconStyles}
        dialogStyles={dialogStyles}
        paperStyles={paperStyles}
        titleStyles={titleStyles}
        actionsStyles={actionsStyles}
        content={
          <>
            <BasicTabs
              renderingStrategy="eager"
              tabs={tabs}
              selectedTab={selectedTabIndex}
              tabsStyles={tabStyles}
              customSeperator={<FloatingLine />}
              changeTab={(selectedTabIndex: number): void => {
                setSelectedTabIndex(selectedTabIndex);
              }}
            />
          </>
        }
        actions={[
          <Button key="cancel" variant="outlined" size="medium" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            variant="contained"
            size="medium"
            onClick={handleConfirm}
            disabled={wallLossUpdating || updateBlisterHeightTypeLoading}
          >
            Confirm
          </Button>,
        ]}
      />
    </>
  );
};
